import { useQuery } from '@apollo/client';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { ActionRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Action_Bool_Exp,
  GetActionsQuery,
  Risk_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetActionsDocument,
  GetRisksFlatDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcActionsToGraphQL } from 'src/hooks/queries/action/useGetActionsRegister';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields, TableRecord } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import { createDataSource } from '../createDataSource';
import { defaultClickthroughFilter } from '../dataSourceHelpers';
import { simpleField, useRelationshipTableProps } from './relationship-helpers';

export type RiskActionRow = TableRecord & {
  Id: string;
  RiskId: string;
  RiskTitle: string;
  RiskTier: string;
  ActionId: string;
  ActionTitle: string;
  ActionStatus: string;
  ActionPriority: string;
};

export default createDataSource({
  group: 'relationship',
  hasAccess: () => true,
  parentTypes: [Parent_Type_Enum.Risk, Parent_Type_Enum.Action],
  documentNode: GetActionsDocument,
  trpcQuery: (trpc) => {
    const queryOptions = trpc.frontend.action.register.queryOptions({});
    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: ActionRegisterResponse) => mapTrpcActionsToGraphQL(data),
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () => ({
    where: useEntityWhereFilter<Action_Bool_Exp>(Parent_Type_Enum.Action),
  }),
  useTablePropsHook: (
    data: GetActionsQuery | undefined,
    options: StatefulTableOptions<RiskActionRow>
  ) => {
    const fields = useMemo<TableFields<RiskActionRow>>(
      () => ({
        RiskTitle: simpleField(i18n.t('risks.columns.risk_name')),
        RiskTier: simpleField(i18n.t('risks.columns.risk_tier')),
        ActionTitle: simpleField(i18n.t('actions.columns.title')),
        ActionStatus: simpleField(i18n.t('actions.columns.status')),
        ActionPriority: simpleField(i18n.t('actions.columns.priority')),
      }),
      []
    );

    const riskWhere = useEntityWhereFilter<Risk_Bool_Exp>(
      Parent_Type_Enum.Risk
    );
    const { data: riskData } = useQuery(GetRisksFlatDocument, {
      variables: { where: riskWhere, riskAssessmentResultsWhere: {} },
    });

    const joinedData = useMemo(() => {
      if (!data?.action || !riskData?.risk) {
        return undefined;
      }

      const riskMap = new Map(riskData.risk.map((r) => [r.Id, r]));

      return data.action.flatMap((action) =>
        action.parents
          .filter(
            (p) => p.parent?.ObjectType === Parent_Type_Enum.Risk && p.parent.Id
          )
          .map((parent) => {
            const risk = riskMap.get(parent.parent!.Id);

            return {
              Id: `${parent.parent!.Id}_${action.Id}`,
              RiskId: parent.parent!.Id,
              RiskTitle: risk?.Title ?? parent.risk?.Title ?? '',
              RiskTier: String(risk?.Tier ?? ''),
              ActionId: action.Id,
              ActionTitle: action.Title ?? '',
              ActionStatus: action.Status ?? '',
              ActionPriority: String(action.Priority ?? ''),
            };
          })
      );
    }, [data?.action, riskData?.risk]);

    return useRelationshipTableProps(
      joinedData,
      fields,
      i18n.t('risk_action_other'),
      options
    );
  },
  entityNamePlural: 'risk_action_other',
  entityNameSingular: 'risk_action_one',
  fields: 'riskActions.fields',
  dashboardFilterConfig: {},
  categoryGetters: [
    {
      id: 'riskTier',
      name: () => i18n.t('risks.columns.risk_tier'),
      categoryGetter: (item: RiskActionRow) => item.RiskTier || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTier'),
    },
    {
      id: 'actionStatus',
      name: () => i18n.t('actions.columns.status'),
      categoryGetter: (item: RiskActionRow) => item.ActionStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionStatus'),
      ratingColourKey: 'action_status',
    },
    {
      id: 'actionPriority',
      name: () => i18n.t('actions.columns.priority'),
      categoryGetter: (item: RiskActionRow) => item.ActionPriority || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionPriority'),
      ratingColourKey: 'priority',
    },
    {
      id: 'riskTitle',
      name: () => i18n.t('risks.columns.risk_name'),
      categoryGetter: (item: RiskActionRow) => item.RiskTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTitle'),
    },
    {
      id: 'actionTitle',
      name: () => i18n.t('actions.columns.title'),
      categoryGetter: (item: RiskActionRow) => item.ActionTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ActionTitle'),
    },
  ],
});
